import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import Publisher from '@/models/Publisher'
import PublisherService from '@/services/PublisherService'
import { useNotify } from '@/composables/Notify'
import { useI18n } from 'vue-i18n'
import { Mutex } from 'async-mutex'

const cacheDays = 1

export const usePublisherStore = defineStore(
  'publisher',
  () => {
    const { notifyError, notifySuccess } = useNotify()
    const { t } = useI18n()

    // state
    const publisherId = ref<string|undefined>(undefined)
    const publishers = ref<Publisher[]>([])
    const fetchedAt = ref<number|undefined>(undefined)
    const loading = ref(false)
    const mutex = new Mutex()

    // getters
    const currentPublisher = computed(() => publishers.value.find(p => p.id === publisherId.value))

    // actions
    const create = (publisher: Partial<Publisher>): Promise<void> => {
      loading.value = true
      return PublisherService.create(publisher).then(response => {
        publishers.value = [...publishers.value, response]
        switchPublisher(response.id)
        notifySuccess(t('common.createSuccess'))
      }).catch(error => {
        notifyError(error)
        return Promise.reject(error)
      }).finally(() => {
        loading.value = false
      })
    }

    const deleteItem = async (id: string): Promise<void> => {
      loading.value = true
      return PublisherService.delete(id).then(() => {
        publishers.value = publishers.value.filter(p => p.id !== id)
        if (publisherId.value === id) publisherId.value = publishers.value[0]?.id
        notifySuccess(t('common.deleteSuccess'))
      }).catch(error => {
        notifyError(error)
        return Promise.reject(error)
      }).finally(() => {
        loading.value = false
      })
    }

    const fetch = () => {
      return mutex.runExclusive(async () => {
        if (loading.value) return
        if (fetchedAt.value && ((new Date().getTime() - fetchedAt.value) < (cacheDays * 24 * 60 * 60 * 1000))) {
          return Promise.resolve(publishers.value)
        }

        loading.value = true
        return PublisherService.list({ per_page: 1000 }).then(response => {
          publishers.value = response.data
          fetchedAt.value = Date.now().valueOf()
          // Every scoped request needs X-Publisher-Id, so never leave the
          // selection empty when the user has something to select.
          if (!currentPublisher.value) {
            publisherId.value = publishers.value[0]?.id
          }
        }).catch(error => {
          notifyError(error)
        }).finally(() => {
          loading.value = false
        })
      })
    }

    const switchPublisher = (id: string) => {
      publisherId.value = id
    }

    const update = (publisher: Partial<Publisher>): Promise<void> => {
      loading.value = true
      return PublisherService.update(publisher).then(response => {
        const index = publishers.value.findIndex(p => p.id === response.id)
        if (index !== -1) {
          publishers.value = [
            ...publishers.value.slice(0, index),
            response,
            ...publishers.value.slice(index + 1),
          ]
        } else {
          publishers.value = [...publishers.value, response]
        }
        notifySuccess(t('common.saveSuccess'))
      }).catch(error => {
        notifyError(error)
      }).finally(() => {
        loading.value = false
      })
    }

    return {
      create,
      currentPublisher,
      deleteItem,
      fetch,
      fetchedAt,
      loading,
      publisherId,
      publishers,
      switchPublisher,
      update,
    }
  },
  {
    persist: import.meta.env.SSR ? false : {
      afterRestore: (context) => {
        context.store.loading = false
      },
    },
  }
)
